/**
 * Utility functions for determining patient report types.
 *
 * Org-specific behaviour (e.g. SSI-first orgs always including SSI content
 * in Not-ADHD reports) is resolved via the centralised workflow config
 * rather than hardcoded org-name string checks.
 *
 * For flat-model orgs (no PPD/SCD hierarchy) the ADHD-path question is
 * answered by the Diagnostic Decision outcome rather than the CI's PPD.
 * isAdhdAssessmentFlow centralises that branching.
 */

import { isAdhdAssessmentFlow } from './conditionChecks';
import { resolveWorkflowConfig } from '../hooks/useWorkflowConfig';

/**
 * Determine the appropriate patient report type based on patient data and organisation.
 *
 * Three possible report types:
 * - "Patient Report - ADHD": ADHD assessment pathway and DD confirmed ADHD
 * - "Patient Report - Not ADHD - CI": Not on ADHD path, SSI was not conducted
 * - "Patient Report - Not ADHD - CI + SSI": Not on ADHD path (or DD ruled out
 *   ADHD), but SSI was conducted (either because the assessment entered the
 *   ADHD flow and then DD ruled it out, or because the org always conducts SSI)
 *
 * Whether the patient is "on the ADHD path" depends on the diagnostic model:
 *  - Hierarchical: PPD = ADHD from CI DF
 *  - Flat: DD outcome (isADHDDiagnosisPositive)
 *
 * Whether SSI content is included in Not-ADHD reports is driven by
 * isSSIAlwaysExpected — an independent config axis from the DF model.
 *
 * @param {Object} patientData - The patient's Firestore document
 * @param {string} [organisation] - The organisation name (optional, used for legacy fallback)
 * @returns {string} The report type for patient report generation
 */
export const determinePatientReportType = (patientData, organisation) => {
  if (!patientData?.dataModel) {
    console.warn('Patient data model is missing, defaulting to standard report type');
    return 'Patient Report - ADHD';
  }

  const wf = resolveWorkflowConfig(patientData, { organisation });
  const adhdPath = isAdhdAssessmentFlow(patientData, wf);

  if (!adhdPath) {
    // Not on the ADHD assessment path.  Decide whether SSI content should
    // be included based on whether this org always expects an SSI to be
    // conducted — this is independent of the flat/hierarchical DF model.
    if (wf.isSSIAlwaysExpected) {
      return "Patient Report - Not ADHD - CI + SSI";
    }
    return "Patient Report - Not ADHD - CI";
  }

  // On the ADHD path — check the Diagnostic Decision outcome to see if
  // ADHD was ultimately confirmed after the SSI deep-dive.
  const diagnosisDecision = patientData.dataModel['4_diagnosticDecision']?.sections?.[1]?.diagnosisDecision;
  const isADHDAfterSSI = diagnosisDecision === "Fully met" || diagnosisDecision === "Partially met";

  if (isADHDAfterSSI) {
    return "Patient Report - ADHD";
  } else {
    return "Patient Report - Not ADHD - CI + SSI";
  }
};